import React, { useState, useEffect } from 'react';
import { MapPin, DollarSign, Users, TrendingUp, AlertCircle } from 'lucide-react';

const RegionalInfrastructureMap = () => {
  const [regionalData, setRegionalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRegion, setSelectedRegion] = useState('all');

  // Fetch data from your existing infrastructure tracker API
  useEffect(() => {
    // This will pull from your existing database
    fetch('https://pivotintel.org/pivotintel/api/regional-summary.php')
      .then(res => res.json())
      .then(data => {
        setRegionalData(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading regional data:', err);
        // Fallback to static data if API not ready yet
        setRegionalData(fallbackData);
        setLoading(false);
      });
  }, []);

  // Fallback data until API is ready
  const fallbackData = [
    {
      region: 'Michigan',
      state: 'MI',
      projectCount: 17,
      totalInvestment: 25000000000,
      constructionJobsMin: 12000,
      constructionJobsMax: 15000,
      permanentJobsMin: 500,
      permanentJobsMax: 800,
      topProjects: [
        { name: 'Saline Township (OpenAI/Oracle)', investment: 7000000000, status: 'operational' },
        { name: 'Grayling (State Land)', investment: null, status: 'under_consideration' },
        { name: 'Augusta Township', investment: null, status: 'ballot_measure' }
      ],
      automationImpact: 'High - Manufacturing base transition',
      costPerPermanentJob: 31250000
    },
    {
      region: 'Stargate Consortium',
      state: 'Multi-state (TX, NM, WY, PA, GA)',
      projectCount: 50,
      totalInvestment: 500000000000,
      constructionJobsMin: 50000,
      constructionJobsMax: 75000,
      permanentJobsMin: 3000,
      permanentJobsMax: 5000,
      topProjects: [
        { name: 'Project Jupiter (New Mexico)', investment: 165000000000, status: 'announced' },
        { name: 'Abilene Cluster (Texas)', investment: null, status: 'construction' },
        { name: 'Multiple Wyoming Sites', investment: null, status: 'proposed' }
      ],
      automationImpact: 'Creating new tech economy, displacing traditional',
      costPerPermanentJob: 100000000
    },
    {
      region: 'Virginia (Northern)',
      state: 'VA',
      projectCount: 45,
      totalInvestment: 75000000000,
      constructionJobsMin: 30000,
      constructionJobsMax: 40000,
      permanentJobsMin: 2500,
      permanentJobsMax: 3500,
      topProjects: [
        { name: 'Loudoun County Expansion', investment: null, status: 'ongoing' },
        { name: 'Prince William County', investment: null, status: 'construction' }
      ],
      automationImpact: 'Mature market - operations focus over construction',
      costPerPermanentJob: 25000000
    },
    {
      region: 'Great Lakes Region',
      state: 'WI, OH, IN, IL',
      projectCount: 35,
      totalInvestment: 45000000000,
      constructionJobsMin: 25000,
      constructionJobsMax: 35000,
      permanentJobsMin: 1800,
      permanentJobsMax: 2500,
      topProjects: [
        { name: 'Microsoft Mount Pleasant (WI)', investment: null, status: 'construction' },
        { name: 'Meta Beaver Dam (WI)', investment: 1000000000, status: 'announced' },
        { name: 'Multiple Ohio Sites', investment: null, status: 'various' }
      ],
      automationImpact: 'Mixed - Tech growth alongside manufacturing decline',
      costPerPermanentJob: 20000000
    }
  ];

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) {
      return `$${(amount / 1000000000).toFixed(1)}B`;
    }
    return `$${(amount / 1000000).toFixed(0)}M`;
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      operational: 'bg-green-100 text-green-800',
      construction: 'bg-blue-100 text-blue-800',
      announced: 'bg-purple-100 text-purple-800',
      proposed: 'bg-yellow-100 text-yellow-800',
      under_consideration: 'bg-gray-100 text-gray-800',
      ballot_measure: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      operational: 'Operational',
      construction: 'Under Construction',
      announced: 'Announced',
      proposed: 'Proposed',
      under_consideration: 'Under Consideration',
      ballot_measure: 'Ballot Measure'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg text-gray-600">Loading regional infrastructure data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold mb-4">Regional AI Infrastructure Buildout</h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Where the $1+ trillion AI infrastructure investment is happening. Real data from active 
          projects tracked daily by PivotIntel's automated intelligence system.
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {formatNumber(regionalData.reduce((sum, r) => sum + r.projectCount, 0))}
          </div>
          <div className="text-sm text-gray-600">Active Projects</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-3xl font-bold text-green-600 mb-2">
            {formatCurrency(regionalData.reduce((sum, r) => sum + r.totalInvestment, 0))}
          </div>
          <div className="text-sm text-gray-600">Total Investment</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-3xl font-bold text-purple-600 mb-2">
            {formatNumber(regionalData.reduce((sum, r) => sum + r.constructionJobsMax, 0))}
          </div>
          <div className="text-sm text-gray-600">Construction Jobs (peak)</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          <div className="text-3xl font-bold text-orange-600 mb-2">
            {formatNumber(regionalData.reduce((sum, r) => sum + r.permanentJobsMax, 0))}
          </div>
          <div className="text-sm text-gray-600">Permanent Jobs</div>
        </div>
      </div>

      {/* Regional Cards */}
      <div className="space-y-6">
        {regionalData.map((region, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-lg border-l-8 border-blue-500 overflow-hidden">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <MapPin className="h-6 w-6 text-blue-600" />
                    <h3 className="text-2xl font-bold">{region.region}</h3>
                  </div>
                  <div className="text-gray-600">{region.state}</div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-blue-600">{region.projectCount}</div>
                  <div className="text-sm text-gray-600">Projects</div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <div className="text-sm text-gray-600">Investment</div>
                  </div>
                  <div className="text-xl font-bold text-green-700">
                    {formatCurrency(region.totalInvestment)}
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <div className="text-sm text-gray-600">Construction Jobs</div>
                  </div>
                  <div className="text-xl font-bold text-blue-700">
                    {formatNumber(region.constructionJobsMin)}-{formatNumber(region.constructionJobsMax)}
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-5 w-5 text-purple-600" />
                    <div className="text-sm text-gray-600">Permanent Jobs</div>
                  </div>
                  <div className="text-xl font-bold text-purple-700">
                    {formatNumber(region.permanentJobsMin)}-{formatNumber(region.permanentJobsMax)}
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <div className="text-sm text-gray-600">Cost/Job</div>
                  </div>
                  <div className="text-lg font-bold text-red-700">
                    {formatCurrency(region.costPerPermanentJob)}
                  </div>
                </div>
              </div>

              {/* Top Projects */}
              <div className="mb-4">
                <h4 className="font-bold mb-3">Major Projects</h4>
                <div className="space-y-2">
                  {region.topProjects.map((project, pidx) => (
                    <div key={pidx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-semibold">{project.name}</div>
                        {project.investment && (
                          <div className="text-sm text-gray-600">
                            Investment: {formatCurrency(project.investment)}
                          </div>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                        {getStatusLabel(project.status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Automation Impact */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-yellow-900 mb-1">Local Automation Impact</div>
                    <div className="text-sm text-yellow-800">{region.automationImpact}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Data Source Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>Live Data:</strong> Regional infrastructure data updates automatically from PivotIntel's 
          project tracking database. Data includes announced projects, construction starts, and operational 
          facilities. Updated daily via automated scrapers monitoring state/local news, planning commission 
          filings, and company announcements.
        </p>
      </div>

      {/* Link to Full Tracker */}
      <div className="text-center">
        <a 
          href="/app/infrastructure" 
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 shadow-lg"
        >
          View Full Infrastructure Tracker
          <TrendingUp className="h-5 w-5" />
        </a>
      </div>
    </div>
  );
};

export default RegionalInfrastructureMap;
